import type { FormGroup } from '@angular/forms';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type {
    HoverEffectsFormGroup,
    NavigationWidgetDataFormType,
    NavigationWidgetDataFormGroup,
} from '@web/widgets/navigation/type';

import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl } from '@angular/forms';

import { EFontStyle, EHorizontalPosition, EMenuLogoPosition, type ESize } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';

import { WebBaseControlTypedSettingsComponent } from '@web/widgets/common/abstract/web-base-control-typed-settings.component';
import { TranslateModule } from '@ngx-translate/core';
import { WebHoverEffectsModule } from '@web/shared/components/web-hover-effects/web-hover-effects.module';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpRadioGroupComponent } from '@ui/sp-radio-group';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';

@Component({
    selector: 'web-style-settings',
    templateUrl: './style-settings.component.html',
    styleUrls: ['./style-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WebSettingsModule,
        SpRadioGroupComponent,
        SpInputNumComponent,
        WebColorPickerComponent,
        WebHoverEffectsModule,
        TranslateModule,
    ],
})
export class StyleSettingsComponent extends WebBaseControlTypedSettingsComponent<NavigationWidgetDataFormGroup> {
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EFontStyle = EFontStyle;
    public readonly FONT_SIZE_MAX = 72;
    public readonly EMenuLogoPosition = EMenuLogoPosition;
    public readonly EHorizontalPosition = EHorizontalPosition;

    // protected initForm(): void {
    //     this.itemForm = this.fb.group({
    //         textColor: null,
    //         menuItemIndent: null,
    //         layoutDirection: null,
    //         fontStyle: this.fb.array([]),
    //         fontSize: null,
    //         hoverEffects: this.fb.group({
    //             type: null,
    //             color: null,
    //             transparency: null,
    //             lineType: null,
    //             lineWidth: null,
    //             animationType: null,
    //         }),
    //     });
    // }
    // protected setFormData(): void {
    //     this.fontStyle.clear({ emitEvent: false });
    //     this.item.fontStyle.forEach((item: EFontStyle) => this.fontStyle.push(this.fb.control(item), { emitEvent: false }));
    //     this.itemForm.patchValue(this.item, { emitEvent: false });
    // }

    public get fontSize(): NavigationWidgetDataFormType['fontSize'] {
        return this.itemForm.get('fontSize') as NavigationWidgetDataFormType['fontSize'];
    }

    public get submenuFontSize(): NavigationWidgetDataFormType['submenuFontSize'] {
        return this.itemForm.get('submenuFontSize') as NavigationWidgetDataFormType['submenuFontSize'];
    }

    public get layoutDirection(): NavigationWidgetDataFormType['layoutDirection'] {
        return this.itemForm.get('layoutDirection') as NavigationWidgetDataFormType['layoutDirection'];
    }

    public get textColor(): NavigationWidgetDataFormType['textColor'] {
        return this.itemForm.get('textColor') as NavigationWidgetDataFormType['textColor'];
    }

    public get submenuTextColor(): NavigationWidgetDataFormType['submenuTextColor'] {
        return this.itemForm.get('submenuTextColor') as NavigationWidgetDataFormType['submenuTextColor'];
    }

    public get menuItemIndent(): NavigationWidgetDataFormType['menuItemIndent'] {
        return this.itemForm.get('menuItemIndent') as NavigationWidgetDataFormType['menuItemIndent'];
    }

    public get fontStyle(): NavigationWidgetDataFormType['fontStyle'] {
        return this.itemForm.get('fontStyle') as NavigationWidgetDataFormType['fontStyle'];
    }

    public get submenuFontStyle(): NavigationWidgetDataFormType['submenuFontStyle'] {
        return this.itemForm.get('submenuFontStyle') as NavigationWidgetDataFormType['submenuFontStyle'];
    }

    public get submenuListBackground(): NavigationWidgetDataFormType['submenuListBackground'] {
        return this.itemForm.get('submenuListBackground') as NavigationWidgetDataFormType['submenuListBackground'];
    }

    public setFontSize(value: number): void {
        this.fontSize.patchValue(value);
    }

    public setSubmenuFontSize(value: number): void {
        this.submenuFontSize.patchValue(value);
    }

    public isActiveTextStyle(fontStyle: EFontStyle): boolean {
        return this.fontStyle.value.includes(fontStyle);
    }

    public isActiveSubmenuTextStyle(fontStyle: EFontStyle): boolean {
        return this.submenuFontStyle.value.includes(fontStyle);
    }

    public setMenuItemIndent(value: string): void {
        this.menuItemIndent.setValue(value as ESize);
    }

    public toggleActiveTextStyle(fontStyle: EFontStyle): void {
        const index = this.fontStyle.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            this.fontStyle.removeAt(index);
        } else {
            this.fontStyle.push(new FormControl(fontStyle));
        }
    }

    public toggleActiveSubmenuTextStyle(fontStyle: EFontStyle): void {
        const index = this.submenuFontStyle.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            this.submenuFontStyle.removeAt(index);
        } else {
            this.submenuFontStyle.push(new FormControl(fontStyle));
        }
    }

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.textColor.setValue(presetName ?? value);
    }

    public setSubmenuTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.submenuTextColor.setValue(presetName ?? value);
    }

    public setSubmenuListBackground({ value, presetName }: ColorPickerEventPayload): void {
        this.submenuListBackground.setValue(presetName ?? value);
    }

    public setLayoutDirection(value: string): void {
        this.layoutDirection.setValue(value as NavigationWidgetDataFormType['layoutDirection']['value']);
    }

    public get hoverEffects(): FormGroup<HoverEffectsFormGroup> {
        return this.itemForm.get('hoverEffects') as FormGroup<HoverEffectsFormGroup>;
    }
}
